import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './scss/Header.scss';
import logo from './images/logo2.svg';
import Navigation from './Navigation';
import { FaBars, FaSync, FaCrown, FaBell } from 'react-icons/fa';
import { checkAuthAndRedirect } from './utils/auth';
import Marquee from './Marquee';
import StoriesButton from './stories/StoriesButton';
import { authService } from './services/authService';
import api from './services/apiService';

function Header() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [teamName, setTeamName] = useState('Hastings Athletic');
  const [childName, setChildName] = useState('');
  const [isParent, setIsParent] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [teamLogo, setTeamLogo] = useState(null);
  const [teamLogoLoaded, setTeamLogoLoaded] = useState(false);
  const navigate = useNavigate();
  const isPro = authService.isPro();
  const isManager = authService.isManager();
  const isSuperAdmin = authService.isSuperAdmin();
  
  // Check if running on iOS/Capacitor
  const isCapacitor = window.Capacitor?.isNativePlatform() || false;
  const isIOS = isCapacitor && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = isCapacitor && window.Capacitor?.getPlatform() === 'android';
  
  // Fetch notifications count
  const fetchNotificationsCount = async () => {
    try {
      const teamId = sessionStorage.getItem('selected_team_id');
      const playerId = sessionStorage.getItem('player_id');
      
      // If there's no player ID (admin, coach, etc.) or team ID, don't try to fetch notifications
      if (!playerId || !teamId) return;
      
      let apiUrl = process.env.REACT_APP_API_URL;
      
      // Get notifications
      const response = await api.get(`${apiUrl}/get_announcements.php`, {
        params: {
          team_id: teamId,
          player_id: playerId
        }
      });
      
      if (response.data.success && response.data.announcements) {
        // Count unread notifications
        const unreadCount = response.data.announcements.filter(
          notification => notification.is_read === "0" || notification.is_read === false || !notification.is_read
        ).length;
        
        setUnreadMessages(unreadCount);
    
      }
    } catch (error) {
      console.error('Error fetching notifications count:', error);
    }
  };
  
  useEffect(() => {
    // Add Android class to body for CSS targeting
    if (isAndroid) {
      document.body.classList.add('android-device');
    } else {
      document.body.classList.remove('android-device');
    }
    
    checkAuthAndRedirect(navigate);

    // Check for token changes - this is a good indication that the user has logged in
    const handleTokenChange = () => {
      console.log('Auth token detected, fetching notifications');
      // Trigger notification fetch on login
      fetchNotificationsCount();
      // Dispatch event to ensure all notification components are updated
      window.dispatchEvent(new Event('notificationUpdate'));
    };

    // Check if we already have a token
    const token = localStorage.getItem('auth_token');
    if (token) {
      handleTokenChange();
    }

    // Add a listener for token changes
    window.addEventListener('storage', function(e) {
      if (e.key === 'auth_token' && e.newValue !== null) {
        handleTokenChange();
      }
    });

    // Initial team name and child name check
    const storedTeamName = sessionStorage.getItem('selected_team_name');
    if (storedTeamName) {
      setTeamName(storedTeamName);
    }
    
    // Check if user is a parent
    const userType = sessionStorage.getItem('user_type');
    const parentFlag = sessionStorage.getItem('is_parent');
    setIsParent(userType === 'parent' || parentFlag === 'true');
    
    // Get child name if available
    if (isParent) {
      const fetchChildName = async () => {
        const playerId = sessionStorage.getItem('player_id');
        if (playerId) {
          try {
            // Try to fetch the child's name from the API
            const response = await fetch(`${process.env.REACT_APP_API_URL}/get_player.php?player_id=${playerId}`);
            const data = await response.json();
            if (data.success && data.player) {
              const fullName = `${data.player.first_name} ${data.player.last_name}`;
              setChildName(fullName);
              // Also store in sessionStorage for future reference
              sessionStorage.setItem('child_name', fullName);
            }
          } catch (error) {
            console.error('Error fetching child name:', error);
          }
        }
      };
      
      // First check if we already have it in sessionStorage
      const storedChildName = sessionStorage.getItem('child_name');
      if (storedChildName) {
        setChildName(storedChildName);
      } else {
        fetchChildName();
      }
    }

    // Function to load team logo
    const loadTeamLogo = (teamId) => {
      if (teamId) {
        const logoUrl = `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-light.png`;
        const img = new Image();
        img.onload = () => {
          setTeamLogo(logoUrl);
          setTeamLogoLoaded(true);
        };
        img.onerror = () => {
          setTeamLogo(null);
          setTeamLogoLoaded(false);
        };
        img.src = logoUrl;
      } else {
        setTeamLogo(null);
        setTeamLogoLoaded(false);
      }
    };

    // Fetch notifications count on login
    fetchNotificationsCount();
    
    // Load initial team logo
    const currentTeamId = sessionStorage.getItem('selected_team_id');
    if (currentTeamId) {
      loadTeamLogo(currentTeamId);
    }

    // Listen for changes to sessionStorage
    const handleStorageChange = (e) => {
      if (e.key === 'selected_team_name') {
        setTeamName(e.newValue || 'Hastings Athletic');
      }
      if (e.key === 'child_name') {
        setChildName(e.newValue || '');
      }
      if (e.key === 'is_parent') {
        setIsParent(e.newValue === 'true');
      }
      // If team changed, refetch notifications and load team logo
      if (e.key === 'selected_team_id') {
        fetchNotificationsCount();
        loadTeamLogo(e.newValue);
      }
    };

    // Listen for custom team name updates
    const handleTeamNameUpdate = (e) => {
      setTeamName(e.detail || 'Hastings Athletic');
    };
    
    // Listen for notification updates
    const handleNotificationUpdate = () => {
      console.log('Notification update event received');
      fetchNotificationsCount();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('teamNameUpdate', handleTeamNameUpdate);
    window.addEventListener('notificationUpdate', handleNotificationUpdate);

    // Refresh notifications every 5 minutes
    const notificationInterval = setInterval(fetchNotificationsCount, 5 * 60 * 1000);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('teamNameUpdate', handleTeamNameUpdate);
      window.removeEventListener('notificationUpdate', handleNotificationUpdate);
      clearInterval(notificationInterval);
      // Cleanup Android class
      document.body.classList.remove('android-device');
    };
  }, [navigate, isParent, isAndroid]);

  const toggleNavigation = () => {
    setIsNavOpen(!isNavOpen);
  };

  const refreshPage = () => {
    window.location.reload();
  };

  const handleNotificationClick = () => {
    navigate('/notifications');
  };

  return (
    <div> 
        <header 
          className={`header ${isIOS ? 'ios-header' : ''} ${isAndroid ? 'android-header' : ''}`}
        >
          <div className="logo-container">
            <img 
              src={teamLogo && teamLogoLoaded ? teamLogo : logo} 
              alt="Logo" 
              className="logo" 
              onClick={() => navigate('/dashboard')} 
              onError={(e) => {
                // If team logo fails to load, fall back to FirstWhistle logo
                if (e.target.src !== logo) {
                  e.target.src = logo;
                  setTeamLogoLoaded(false);
                }
              }}
            />
            <div className="site-title-container">
              <div className="site-title" style={{ cursor: 'default' }}>
                <span className="team-name">{teamName}</span>
                {isParent && childName && (
                  <span className="child-name">{childName}</span>
                )}
              </div>
              {isPro && <FaCrown className="pro-badge" title="Pro Member" />}
            </div>
          </div>
          <div className="menu-icons">
            <StoriesButton />
            <div className="mail-button-container" onClick={handleNotificationClick}>
              <FaBell className="notification-icon" title="Notifications" />
              {unreadMessages > 0 && (
                <span className="notification-badge">{unreadMessages}</span>
              )}
            </div>
            {/* <FaSync className="refresh-icon" onClick={refreshPage} /> */}
            <FaBars className="burger-menu" onClick={toggleNavigation} />
          </div>
        </header>
        <div className={`fullscreen-nav ${isNavOpen ? 'open' : ''}`}>
          <Navigation onNavigate={toggleNavigation} />
          <button className="close-nav" onClick={toggleNavigation}>Close</button>
        </div>
        <Marquee />
    </div>
  );
}

export default React.memo(Header); 